/**
 * Sample on-screen video health for diagnostics (same paint bar as live preview).
 * Prefer the largest visible element under the live-preview root; if that subtree has
 * no `<video>` nodes, fall back to `document` (stale/detached preview root, or videos
 * mounted outside the attributed layer during transitions).
 */

import {
  resolveVisibleVideoSrcScheme,
  type TVisibleVideoSrcScheme
} from '@/helpers/playerDiagnostics/resolveVisibleVideoSrcScheme'

const MIN_VISIBLE_MEDIA_PX = 32

export interface IVisibleVideoDiagnosticsSample {
  elementCount: number
  /** Best candidate readyState (0–4), or null when no video element. */
  readyState: number | null
  videoWidth: number | null
  networkState: number | null
  mediaErrorCode: number | null
  /** Classification of best candidate currentSrc/src (blob vs CDN vs HLS). */
  srcScheme: TVisibleVideoSrcScheme | null
  /** True when at least one visible video can paint (readyState ≥ 2 and videoWidth > 0). */
  paintReady: boolean
}

const EMPTY_SAMPLE: IVisibleVideoDiagnosticsSample = {
  elementCount: 0,
  readyState: null,
  videoWidth: null,
  networkState: null,
  mediaErrorCode: null,
  srcScheme: null,
  paintReady: false
}

function asHtmlVideoElement(el: Element): HTMLVideoElement | null {
  if (typeof HTMLVideoElement !== 'undefined' && el instanceof HTMLVideoElement) {
    return el
  }
  // Some WebView / test doubles expose `<video>` without a cross-realm instanceof match.
  if ((el as HTMLElement).tagName === 'VIDEO') {
    return el as HTMLVideoElement
  }
  return null
}

function isElementVisiblyLaidOut(el: Element): boolean {
  if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    const style = window.getComputedStyle(el)
    if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) {
      return false
    }
  }
  const rect = el.getBoundingClientRect()
  return rect.width >= MIN_VISIBLE_MEDIA_PX && rect.height >= MIN_VISIBLE_MEDIA_PX
}

function scoreVideo(el: HTMLVideoElement): number {
  const rect = el.getBoundingClientRect()
  const area = Math.max(0, rect.width) * Math.max(0, rect.height)
  const paintBonus = el.readyState >= 2 && el.videoWidth > 0 ? 1_000_000_000 : 0
  return area + paintBonus
}

/** Collect video diagnostics from a single scope (exported for unit tests). */
export function sampleVisibleVideoDiagnosticsInScope(
  scope: ParentNode
): IVisibleVideoDiagnosticsSample {
  const videos = [...scope.querySelectorAll('video')]
    .map(asHtmlVideoElement)
    .filter((el): el is HTMLVideoElement => el != null)
  if (videos.length === 0) {
    return EMPTY_SAMPLE
  }
  const visible = videos.filter(isElementVisiblyLaidOut)
  const pool = visible.length > 0 ? visible : videos
  let best = pool[0]!
  let bestScore = scoreVideo(best)
  for (let i = 1; i < pool.length; i++) {
    const el = pool[i]!
    const score = scoreVideo(el)
    if (score > bestScore) {
      best = el
      bestScore = score
    }
  }
  const mediaErrorCode = best.error?.code
  const paintReady = pool.some(el => el.readyState >= 2 && el.videoWidth > 0)
  return {
    elementCount: videos.length,
    readyState: best.readyState,
    videoWidth: best.videoWidth,
    networkState: best.networkState,
    mediaErrorCode: mediaErrorCode != null && mediaErrorCode >= 1 ? mediaErrorCode : null,
    srcScheme: resolveVisibleVideoSrcScheme(best.currentSrc || best.src),
    paintReady
  }
}

function defaultPreviewRoot(): Element | null {
  if (typeof document === 'undefined') {
    return null
  }
  return document.querySelector('[data-player-live-preview-root]')
}

/**
 * Collect video diagnostics from `[data-player-live-preview-root]`, else `document`.
 * When the preferred root is empty, also try `document` so a stale/empty preview attribute
 * cannot zero out `visibleVideoElementCount` while videos still play elsewhere in the tree.
 */
export function sampleVisibleVideoDiagnostics(
  root: ParentNode | Document | Element | null = defaultPreviewRoot()
): IVisibleVideoDiagnosticsSample {
  const preferred = root
  if (preferred != null) {
    const fromPreferred = sampleVisibleVideoDiagnosticsInScope(preferred as ParentNode)
    if (fromPreferred.elementCount > 0) {
      return fromPreferred
    }
  }
  if (
    typeof document !== 'undefined'
    && preferred !== document
  ) {
    return sampleVisibleVideoDiagnosticsInScope(document)
  }
  return EMPTY_SAMPLE
}
